import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Stack,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ArrowBack, Delete, Search } from "@mui/icons-material";
import { useParams, useOutletContext } from "react-router-dom";
import { useSnackbar } from "notistack";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import ConfirmDialog from "components/ConfirmDialog";
import SidePanel from "components/SidePanel";
import usePaginatedResource from "hooks/usePaginatedResource";
import useDebounce from "hooks/useDebounce";
import { ROWS_PER_PAGE_OPTIONS } from "config/pagination";
import { formatDateTime } from "utils/datetime";

import {
  listFormSubmissions,
  updateFormSubmission,
  deleteFormSubmission,
  getEventsByFormSubmissionId,
  getEventExecutions,
  FORM_SUBMISSION_STATUSES,
} from "api/endpoints/formSubmissions";
import { getForm } from "api/endpoints/forms";

// Render a submission field value (handles arrays from multi-select fields).
const cellValue = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  if (Array.isArray(val)) return val.join(", ");
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

// At most this many form fields become table columns; the rest are visible in
// the detail panel.
const MAX_FIELD_COLUMNS = 2;

const STATUS_COLORS = { pending: "warning", processed: "success", spam: "error" };

const renderStatusChip = (status) => (
  <Chip
    label={status || "—"}
    color={STATUS_COLORS[status] || "default"}
    size="small"
    sx={{ textTransform: "capitalize" }}
  />
);

// Event-execution detail — rendered inside the panel's Events tab.
function EventDetail({ event }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Event Details
        </Typography>
        <Typography variant="body2">
          <strong>ID:</strong> {event.id}
        </Typography>
        <Typography variant="body2">
          <strong>Type:</strong> {event.event_type}
        </Typography>
        <Typography variant="body2">
          <strong>Aggregate Type:</strong> {event.aggregate_type}
        </Typography>
        <Typography variant="body2">
          <strong>Aggregate ID:</strong> {event.aggregate_id}
        </Typography>
        <Typography variant="body2">
          <strong>Trace ID:</strong> {event.trace_id}
        </Typography>
        <Typography variant="body2">
          <strong>Created At:</strong> {formatDateTime(event.created_at)}
        </Typography>
      </Paper>

      {event.executions?.length > 0 ? (
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={600}>
            Executions ({event.executions.length})
          </Typography>

          {event.executions.map((exe, idx) => (
            <Paper key={exe.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body1" fontWeight={600}>
                {idx + 1}. {exe.handler_name}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" mt={0.5}>
                <Chip label={exe.status} color="success" size="small" />
                <Typography variant="caption" color="text.secondary">
                  Duration: {exe.processing_duration_in_ms} ms
                </Typography>
              </Stack>

              {exe.metadata?.execution_pathway?.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Execution Pathway
                  </Typography>
                  <List dense disablePadding>
                    {exe.metadata.execution_pathway.map((step, stepIdx) => (
                      <ListItem
                        key={stepIdx}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          borderBottom: "1px solid #eee",
                          py: 1,
                        }}
                      >
                        <Typography fontWeight={500}>
                          {stepIdx + 1}. {step.name}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={step.status}
                            color={
                              step.status === "completed"
                                ? "success"
                                : step.status === "failed"
                                ? "error"
                                : "warning"
                            }
                          />
                          <Typography variant="caption" color="text.secondary">
                            {step.duration_ms} ms
                          </Typography>
                        </Stack>
                        {step.details && (
                          <Box
                            component="pre"
                            sx={{
                              fontSize: "0.8rem",
                              bgcolor: "#f9f9f9",
                              p: 1,
                              borderRadius: 1,
                              width: "100%",
                              overflowX: "auto",
                            }}
                          >
                            {JSON.stringify(step.details, null, 2)}
                          </Box>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary">No executions found.</Typography>
      )}
    </Box>
  );
}

EventDetail.propTypes = {
  event: PropTypes.object.isRequired,
};

export default function FormSubmissionsPage() {
  const { websiteId } = useOutletContext();
  const { formId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [form, setForm] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Detail panel
  const [panelSubmission, setPanelSubmission] = useState(null);
  const [panelTab, setPanelTab] = useState("details");
  const [statusSaving, setStatusSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Event-execution drill-down (shown inside the panel's Events tab)
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(false);

  // Load the form so the table can label columns and the panel can label fields.
  useEffect(() => {
    if (!websiteId || !formId) return;
    getForm(websiteId, formId)
      .then(setForm)
      .catch(() => setForm(null));
  }, [websiteId, formId]);

  const fetchSubmissions = useCallback(
    (pageParams) => {
      if (!websiteId || !formId) return { items: [], total: 0 };
      return listFormSubmissions(websiteId, formId, { ...pageParams, search: debouncedSearch });
    },
    [websiteId, formId, debouncedSearch]
  );

  const {
    rows: submissions,
    total,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    refresh,
  } = usePaginatedResource(fetchSubmissions);

  // A new search term restarts at the first page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, setPage]);

  // Resolve a submission data key to its form-field label.
  const fieldLabel = (key) => {
    const match = form?.schema?.fields?.find((f) => (f.name || f.id) === key);
    return match?.label || key;
  };

  const closeEventDetail = () => {
    setSelectedEventId(null);
    setSelectedEvent(null);
  };

  const openPanel = async (submission) => {
    setPanelSubmission(submission);
    setPanelTab("details");
    closeEventDetail();
    setEvents([]);
    setEventsLoading(true);
    try {
      const eventData = await getEventsByFormSubmissionId(submission.form_submission_id);
      setEvents(eventData.events || []);
    } catch (error) {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const closePanel = () => {
    setPanelSubmission(null);
    closeEventDetail();
  };

  const handleStatusChange = async (newStatus) => {
    if (!panelSubmission || newStatus === panelSubmission.status) return;
    const id = panelSubmission.form_submission_id;
    setStatusSaving(true);
    try {
      await updateFormSubmission(websiteId, formId, id, { status: newStatus });
      setPanelSubmission((prev) => (prev ? { ...prev, status: newStatus } : prev));
      enqueueSnackbar("Status updated", { variant: "success" });
      refresh();
    } catch {
      enqueueSnackbar("Failed to update status", { variant: "error" });
    } finally {
      setStatusSaving(false);
    }
  };

  const handleEventClick = async (eventId) => {
    setSelectedEventId(eventId);
    setSelectedEvent(null);
    setEventLoading(true);
    try {
      const eventDetails = await getEventExecutions(eventId);
      setSelectedEvent(eventDetails);
    } catch (error) {
      setSelectedEvent(null);
    } finally {
      setEventLoading(false);
    }
  };

  const confirmDelete = async () => {
    await deleteFormSubmission(websiteId, formId, confirmDeleteId);
    setConfirmDeleteId(null);
    if (panelSubmission?.form_submission_id === confirmDeleteId) closePanel();
    refresh();
  };

  // Up to MAX_FIELD_COLUMNS columns, derived from the form schema (falls back to
  // the union of keys present in the loaded submissions).
  const fieldColumns = useMemo(() => {
    const makeColumn = (key, label) => ({
      field: `field_${key}`,
      headerName: label || key,
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (params) => cellValue(params.row.data?.[key]),
    });

    const schemaFields = form?.schema?.fields;
    if (Array.isArray(schemaFields) && schemaFields.length > 0) {
      return schemaFields
        .slice(0, MAX_FIELD_COLUMNS)
        .map((f) => makeColumn(f.name || f.id, f.label));
    }

    const keys = new Set();
    submissions.forEach((s) => Object.keys(s.data || {}).forEach((k) => keys.add(k)));
    return [...keys].slice(0, MAX_FIELD_COLUMNS).map((k) => makeColumn(k));
  }, [form, submissions]);

  const columns = [
    ...fieldColumns,
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => renderStatusChip(params.value),
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDeleteId(params.row.form_submission_id);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const dataEntries = Object.entries(panelSubmission?.data || {});

  // Status options for the panel select — include the current value even if it
  // is not one of the known statuses, to avoid an MUI out-of-range warning.
  const currentStatus = panelSubmission?.status;
  const statusOptions =
    currentStatus && !FORM_SUBMISSION_STATUSES.includes(currentStatus)
      ? [currentStatus, ...FORM_SUBMISSION_STATUSES]
      : FORM_SUBMISSION_STATUSES;

  return (
    <MDBox>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          {form?.name ? `${form.name} — Submissions` : "Form Submissions"}
        </Typography>
        <TextField
          placeholder="Search submissions..."
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 280 }}
        />
      </Stack>

      {/* Table */}
      {!loading && submissions.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: 3,
            color: "text.secondary",
            fontSize: "1.1rem",
          }}
        >
          No submissions found.
        </Paper>
      ) : (
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            p: 2,
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { py: 1 },
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
        >
          <DataGrid
            rows={submissions}
            columns={columns}
            getRowId={(row) => row.form_submission_id}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={total}
            pageSize={pageSize}
            page={page}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowClick={(params) => openPanel(params.row)}
            disableRowSelectionOnClick
            disableSelectionOnClick
            autoHeight
          />
        </Paper>
      )}

      {/* Detail panel — submission fields + events */}
      <SidePanel
        open={Boolean(panelSubmission)}
        onClose={closePanel}
        title="Submission"
        subtitle={formatDateTime(panelSubmission?.created_at)}
        width={520}
      >
        {panelSubmission && (
          <>
            <Box mb={2}>
              <Select
                size="small"
                value={panelSubmission.status || ""}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={statusSaving}
                renderValue={(v) => renderStatusChip(v)}
                sx={{ minWidth: 150 }}
              >
                {statusOptions.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: "capitalize" }}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Tabs
              value={panelTab}
              onChange={(e, value) => {
                setPanelTab(value);
                closeEventDetail();
              }}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Details" value="details" />
              <Tab label="Events" value="events" />
            </Tabs>

            {panelTab === "details" &&
              (dataEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This submission has no field data.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {dataEntries.map(([key, value]) => (
                    <Box key={key}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {fieldLabel(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                        {cellValue(value)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ))}

            {panelTab === "events" && selectedEventId !== null && (
              <>
                <MDButton
                  variant="text"
                  color="info"
                  startIcon={<ArrowBack />}
                  onClick={closeEventDetail}
                  sx={{ mb: 2, pl: 0 }}
                >
                  Back to events
                </MDButton>
                {eventLoading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : selectedEvent ? (
                  <EventDetail event={selectedEvent} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No event details found.
                  </Typography>
                )}
              </>
            )}

            {panelTab === "events" &&
              selectedEventId === null &&
              (eventsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : events.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No related events found.
                </Typography>
              ) : (
                <List disablePadding>
                  {events.map((event) => (
                    <Paper
                      key={event.id}
                      elevation={1}
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        p: 1.5,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                      onClick={() => handleEventClick(event.id)}
                    >
                      <ListItem disablePadding>
                        <ListItemText
                          primary={<Typography fontWeight={500}>{event.event_type}</Typography>}
                          secondary={`Created at: ${formatDateTime(event.created_at)}`}
                        />
                      </ListItem>
                    </Paper>
                  ))}
                </List>
              ))}
          </>
        )}
      </SidePanel>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete Submission"
        message="Are you sure you want to delete this submission?"
        confirmText="Delete"
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteId(null)}
      />
    </MDBox>
  );
}
